'use client'

import { HTMLAttributes, PropsWithChildren, useEffect, useRef } from 'react'

import { cn } from '@/lib/utils'

type Props = {
  borderWidth?: number
  borderRadius?: number
  progress: number
} & Pick<HTMLAttributes<HTMLDivElement>, 'className'>

export const ProgressBorder = ({
  borderRadius = 8,
  borderWidth = 6,
  children,
  progress,
  ...props
}: PropsWithChildren<Props>) => {
  const containerRef = useRef<HTMLDivElement>(null)
  const rectRef = useRef<SVGRectElement>(null)

  useEffect(() => {
    try {
      const length = rectRef.current?.getTotalLength()
      if (!length || !rectRef.current) return
      rectRef.current.style.strokeDasharray = `${length}`
      rectRef.current.style.strokeDashoffset = `${length - (progress / 100) * length}`
    } catch (error) {
      console.warn('ProgressBorder: Unable to set progress border', error)
    }
  }, [progress])

  return (
    <div className={cn(`relative w-fit h-fit`, props.className)} ref={containerRef}>
      {/* Background Track */}
      <svg
        className={cn(
          'absolute inset-0 w-full h-full pointer-events-none text-neutral-200 dark:text-neutral-800',
          {},
        )}
        style={{ borderRadius }}
      >
        <rect
          fill="none"
          height="100%"
          rx={borderRadius}
          ry={borderRadius}
          stroke="currentColor"
          strokeWidth={borderWidth}
          width="100%"
        />
      </svg>

      {/* Progress Indicator */}
      <svg
        className={cn('absolute inset-0 w-full h-full pointer-events-none text-inherit', {})}
        style={{ borderRadius }}
      >
        <rect
          fill="none"
          height="100%"
          ref={rectRef}
          rx={borderRadius}
          ry={borderRadius}
          stroke="currentColor"
          strokeWidth={borderWidth}
          style={{
            transition: 'stroke-dashoffset 0.6s linear',
          }}
          width="100%"
        />
      </svg>

      <div className="relative size-full" style={{ padding: borderWidth }}>
        {children}
      </div>
    </div>
  )
}
